import type { CMSContent, PortfolioItem } from "../app-types/index";

export const fallbackPortfolioItems: PortfolioItem[] = [
  {
    id: "por_murtara",
    title: "Murtara Group",
    slug: "murtara",
    summary:
      "Website for Murtara Group, a trucking and logistics company offering dry van shipping, power-only hauling, and driver recruiting.",
    category: "Logistics & Transportation",
    services: ["Business Website", "Service Pages", "Career Page", "Launch Setup"],
    year: "2024",
    demoUrl: "https://murtara.com",
    githubUrl: "",
    coverImage: "",
    featured: true,
    published: true,
    createdAt: "2026-06-27T00:00:00Z",
    updatedAt: "2026-06-27T00:00:00Z"
  }
];

export const cmsContent: CMSContent = {
  homepage: {
    headline: "Let's Get Your Website Online",
    subheadline:
      "Aladdin Shenewa designs, builds, launches, and maintains premium business websites on Cloudflare infrastructure with conversion, speed, and trust baked into every page.",
    primaryCta: "Get Started",
    secondaryCta: "View Portfolio"
  },
  services: [
    {
      id: "website-design",
      title: "Website Design",
      icon: "LayoutTemplate",
      description:
        "A polished visual system, responsive layouts, and conversion-focused page structure designed around your offer.",
      deliverables: ["Design direction", "Responsive layouts", "Interaction states", "Launch-ready UI system"]
    },
    {
      id: "business-websites",
      title: "Business Websites",
      icon: "BriefcaseBusiness",
      description:
        "Fast, secure company websites that explain what you do clearly and make it easy for customers to take action.",
      deliverables: ["Service pages", "Contact funnels", "Analytics setup", "Accessible content structure"]
    },
    {
      id: "landing-pages",
      title: "Landing Pages",
      icon: "MousePointerClick",
      description:
        "High-intent pages for campaigns, launches, and paid traffic with tight messaging and measurable calls to action.",
      deliverables: ["Offer strategy", "Hero and CTA system", "Lead capture", "A/B-ready sections"]
    },
    {
      id: "cloudflare-setup",
      title: "Cloudflare Setup",
      icon: "Cloud",
      description:
        "Modern edge delivery with Cloudflare Pages, Workers, D1, R2, caching rules, DNS, redirects, and security headers.",
      deliverables: ["Pages deployment", "Worker API", "D1 and R2 bindings", "Edge caching rules"]
    },
    {
      id: "domain-setup",
      title: "Domain Setup",
      icon: "Globe2",
      description:
        "Domain connection, DNS records, redirects, canonical URLs, email records, and launch checks handled carefully.",
      deliverables: ["DNS records", "Canonical routing", "Email authentication records", "Launch verification"]
    },
    {
      id: "hosting",
      title: "Hosting",
      icon: "ServerCog",
      description:
        "Reliable hosting architecture on Cloudflare with sensible environments, deploy previews, and rollback paths.",
      deliverables: ["Cloudflare Pages", "Environment variables", "Deployment checks", "Rollback workflow"]
    },
    {
      id: "seo",
      title: "SEO",
      icon: "SearchCheck",
      description:
        "Technical SEO and content foundations covering metadata, OpenGraph, sitemap, internal linking, and indexability.",
      deliverables: ["Metadata system", "Sitemap", "Structured content", "Indexing readiness"]
    },
    {
      id: "optimization",
      title: "Optimization",
      icon: "Gauge",
      description:
        "Performance audits, Core Web Vitals improvements, code splitting, image strategy, caching, and accessibility checks.",
      deliverables: ["Lighthouse improvements", "Lazy loading", "Bundle review", "Accessibility fixes"]
    },
    {
      id: "maintenance",
      title: "Maintenance",
      icon: "ShieldCheck",
      description:
        "Ongoing updates, monitoring, backups, CMS content edits, dependency care, and small improvements after launch.",
      deliverables: ["Update windows", "Health checks", "Content support", "Security reviews"]
    },
    {
      id: "ssl",
      title: "SSL",
      icon: "LockKeyhole",
      description:
        "HTTPS, SSL mode, redirect rules, mixed-content checks, and certificate status monitoring through Cloudflare.",
      deliverables: ["HTTPS enforcement", "SSL mode review", "Certificate checks", "Security headers"]
    }
  ],
  pricing: [
    {
      id: "starter",
      name: "Starter",
      price: "$120 + domain",
      billingNote: "Best for simple launches and fast online presence.",
      description: "A clean, fast starter website with domain connection, essential SEO, and Cloudflare launch support.",
      features: [
        "One-page business website",
        "Mobile-first responsive design",
        "Domain and SSL setup",
        "Contact form",
        "Basic SEO metadata",
        "Cloudflare Pages deployment"
      ],
      cta: "Start Project"
    },
    {
      id: "professional",
      name: "Professional",
      price: "Custom Quote",
      billingNote: "For businesses that need a polished multi-page website.",
      description:
        "A custom website with service pages, CMS-ready content, analytics, optimization, and launch support.",
      features: [
        "Multi-page website",
        "CMS-editable sections",
        "Portfolio or blog",
        "Advanced SEO structure",
        "Performance optimization",
        "Client dashboard access"
      ],
      cta: "Request Quote",
      highlighted: true
    },
    {
      id: "enterprise",
      name: "Enterprise",
      price: "Contact",
      billingNote: "For complex platforms, internal tools, and ongoing delivery.",
      description:
        "A tailored web platform with authentication, dashboards, database, file storage, automation, and maintenance.",
      features: [
        "Custom web application",
        "Clerk authentication and RBAC",
        "Cloudflare D1 and R2",
        "Admin dashboard",
        "Integrations and automations",
        "Maintenance retainer option"
      ],
      cta: "Let's Talk"
    }
  ],
  faq: [
    {
      question: "Is Aladdin Shenewa a portfolio site or a client platform?",
      answer:
        "This platform is built as a service business website with a client dashboard, admin tools, messaging, files, invoices, CMS editing, and Cloudflare-backed API infrastructure."
    },
    {
      question: "Do I need to already own a domain?",
      answer:
        "No. Domain setup is available, including registrar guidance, DNS records, SSL, redirects, and Cloudflare configuration."
    },
    {
      question: "Can I edit website content after launch?",
      answer:
        "Yes. Homepage, service, pricing, portfolio, blog, testimonial, and footer content are modeled for CMS editing through the admin dashboard."
    },
    {
      question: "Where are uploads stored?",
      answer:
        "Uploads are stored in a private Cloudflare R2 bucket. D1 keeps the file metadata, project association, ownership, and audit timestamps."
    },
    {
      question: "How is access protected?",
      answer:
        "Authentication runs through Clerk. The Worker verifies Clerk JWTs and enforces role-based access for client and admin routes."
    }
  ],
  footer: {
    tagline: "Premium websites, Cloudflare infrastructure, and growth-ready web systems.",
    email: "aladdinshenewa@outlook.com"
  }
};
